import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Cloud, Thermometer, Wind, Droplets, AlertTriangle, RefreshCw, Loader2, Sun, CloudRain, Snowflake } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const severityConfig = {
  high: { bg: 'bg-destructive/10', border: 'border-destructive/30', text: 'text-destructive', icon: AlertTriangle },
  medium: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-600', icon: AlertTriangle },
  low: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-600', icon: Cloud },
  info: { bg: 'bg-primary/10', border: 'border-primary/30', text: 'text-primary', icon: Sun },
};

const WeatherAlerts = () => {
  const { getAuthHeader } = useAuth();
  const [weather, setWeather] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [extendedForecast, setExtendedForecast] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWeather = async () => {
    setLoading(true);
    try {
      const [currentRes, alertsRes, forecastRes] = await Promise.all([
        axios.get(`${API_URL}/api/weather/current`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/weather/alerts`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/weather/forecast`, { headers: getAuthHeader() })
      ]);
      
      setWeather(currentRes.data);
      setAlerts(alertsRes.data.alerts || []);
      
      // Split forecast into hourly (first 8 items) and extended (next days)
      const allForecast = forecastRes.data.forecast || [];
      setForecast(allForecast.slice(0, 8)); // First 8 hours
      
      // Group remaining by day for extended forecast
      const dailyMap = {};
      allForecast.forEach(item => {
        const date = new Date(item.dt * 1000);
        const dayKey = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        const hour = date.getHours();
        
        // Use midday temps (10am-2pm) or first available for the day
        if (!dailyMap[dayKey] || (hour >= 10 && hour <= 14)) {
          dailyMap[dayKey] = {
            ...item,
            dayLabel: dayKey,
            isToday: date.toDateString() === new Date().toDateString()
          };
        }
      });
      
      // Take up to 7 days
      const extended = Object.values(dailyMap).slice(0, 7);
      setExtendedForecast(extended);
    } catch (error) {
      console.error('Failed to fetch weather:', error);
      toast.error('Failed to load weather data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeather();
  }, []);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
  };

  const getWeatherIcon = (description) => {
    const desc = description?.toLowerCase() || '';
    if (desc.includes('rain') || desc.includes('shower')) return <CloudRain className="w-5 h-5 text-blue-500" />;
    if (desc.includes('snow')) return <Snowflake className="w-5 h-5 text-cyan-400" />;
    if (desc.includes('cloud')) return <Cloud className="w-5 h-5 text-gray-400" />;
    return <Sun className="w-5 h-5 text-yellow-500" />;
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="weather-loading">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Weather Alerts</h1>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="weather-alerts">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
          Weather Alerts
        </h1>
        <Button 
          variant="outline" 
          size="icon"
          onClick={fetchWeather}
          data-testid="refresh-weather-btn"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Current Conditions - Compact Mobile Layout */}
      {weather && !weather.error && (
        <Card className="card-farm" data-testid="current-weather-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">
              Current Conditions
              {weather.location && <span className="font-normal text-muted-foreground ml-2 text-sm">• {weather.location}</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                <Thermometer className="w-5 h-5 text-secondary flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xl font-heading font-bold text-foreground">{Math.round(weather.temp)}°F</p>
                  <p className="text-xs text-muted-foreground truncate">Feels {Math.round(weather.feels_like)}°</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                <Wind className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xl font-heading font-bold text-foreground">{Math.round(weather.wind_speed)}</p>
                  <p className="text-xs text-muted-foreground">mph wind</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                <Droplets className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xl font-heading font-bold text-foreground">{weather.humidity}%</p>
                  <p className="text-xs text-muted-foreground">humidity</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                {getWeatherIcon(weather.description)}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground capitalize truncate">{weather.description}</p>
                  <p className="text-xs text-muted-foreground">conditions</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Alerts */}
      <Card className="card-farm" data-testid="alerts-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-secondary" />
            Farm Alerts
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {alerts.length > 0 ? (
            alerts.map((alert, index) => {
              const config = severityConfig[alert.severity] || severityConfig.info;
              const IconComponent = config.icon;
              
              return (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border ${config.bg} ${config.border} animate-fade-in`}
                  data-testid={`alert-${alert.type}`}
                >
                  <div className="flex items-start gap-2">
                    <IconComponent className={`w-4 h-4 ${config.text} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className={`font-heading font-semibold ${config.text} text-sm`}>{alert.title}</h3>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${config.bg} ${config.text} font-medium uppercase`}>
                          {alert.severity}
                        </span>
                      </div>
                      <p className="text-foreground text-sm mb-2">{alert.message}</p>
                      <div className="text-sm">
                        <span className="font-medium text-muted-foreground">Action: </span>
                        <span className="text-foreground">{alert.action}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-muted-foreground py-4 text-center">No active alerts</p>
          )}
        </CardContent>
      </Card>

      {/* Hourly Forecast - Vertical Layout */}
      {forecast.length > 0 && (
        <Card className="card-farm" data-testid="hourly-forecast-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Hourly Forecast</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {forecast.map((item, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-sm font-medium text-muted-foreground w-16 flex-shrink-0">
                    {formatTime(item.dt)}
                  </span>
                  {getWeatherIcon(item.description)}
                  <span className="text-sm text-foreground capitalize truncate">
                    {item.description}
                  </span>
                </div>
                <span className="text-lg font-heading font-bold text-foreground ml-2">
                  {Math.round(item.temp)}°
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* 7-Day Extended Forecast */}
      {extendedForecast.length > 1 && (
        <Card className="card-farm" data-testid="extended-forecast-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">7-Day Forecast</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {extendedForecast.map((day, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  day.isToday ? 'bg-primary/10 border border-primary/20' : 'bg-muted/30 hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className={`text-sm font-medium w-24 flex-shrink-0 ${day.isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                    {day.isToday ? 'Today' : day.dayLabel}
                  </span>
                  {getWeatherIcon(day.description)}
                  <span className="text-sm text-foreground capitalize truncate hidden sm:block">
                    {day.description}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-heading font-bold text-foreground">
                    {Math.round(day.temp)}°
                  </span>
                  {day.humidity && (
                    <span className="text-xs text-muted-foreground hidden sm:block">
                      {day.humidity}%
                    </span>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WeatherAlerts;
